import type { LucideIcon } from "lucide-react";
import { Brain, Bug, FlaskConical, Music, FolderHeart, Sparkles, FileText, Trash2, Terminal, Globe, Sliders, Trophy, Gauge } from "lucide-react";
import type { ReactNode } from "react";
import { PROJECTS } from "@/data/projects";
import { BRAIN_FILES } from "@/data/brain";
import { MyBrain } from "@/windows/MyBrain";
import { TabsBrowser } from "@/windows/TabsBrowser";
import { BugsIFixed } from "@/windows/BugsIFixed";
import { Experiments } from "@/windows/Experiments";
import { ProjectWindow } from "@/windows/ProjectWindow";
import { OutsideTerminal } from "@/windows/OutsideTerminal";
import { UnnecessaryThoughts } from "@/windows/UnnecessaryThoughts";
import { FutureExe } from "@/windows/FutureExe";
import { ResumePanel } from "@/windows/ResumePanel";
import { RecycleBin } from "@/windows/RecycleBin";
import { TerminalApp } from "@/windows/TerminalApp";
import { PersonalitySettings } from "@/windows/PersonalitySettings";
import { AchievementsPanel } from "@/windows/AchievementsPanel";
import { PerformancePanel } from "@/windows/PerformancePanel";
import { FileWindow } from "@/windows/FileWindow";

export interface WindowDef {
    id: string;
    title: string;
    label?: string;
    icon: LucideIcon;
    color: string;
    tileBg: string;
    width?: number;
    render: () => ReactNode;
}

export const DESKTOP_ICONS = [
    "my_brain",
    "bugs_i_fixed",
    "experiments",
    "outside_the_terminal",
    "unnecessary_thoughts",
    "future_exe",
    "resume_pdf",
    "recycle_bin",
    "terminal",
    "tabs",
    "personality",
];

const STATIC: Record<string, WindowDef> = {
    my_brain: {
        id: "my_brain",
        title: "my_brain // personality & storytelling",
        label: "my_brain",
        icon: Brain,
        color: "#A855F7",
        tileBg: "#F3E8FF",
        width: 620,
        render: () => <MyBrain />,
    },
    bugs_i_fixed: {
        id: "bugs_i_fixed",
        title: "bugs_i_fixed // case: the two-minute problem",
        label: "bugs_i_fixed",
        icon: Bug,
        color: "#FF6B6B",
        tileBg: "#FFE8E8",
        width: 700,
        render: () => <BugsIFixed />,
    },
    experiments: {
        id: "experiments",
        title: "experiments // project lab",
        label: "experiments",
        icon: FlaskConical,
        color: "#2563EB",
        tileBg: "#EFF6FF",
        width: 780,
        render: () => <Experiments />,
    },
    outside_the_terminal: {
        id: "outside_the_terminal",
        title: "outside_the_terminal // life beyond code",
        label: "outside_the_terminal",
        icon: Music,
        color: "#EAB308",
        tileBg: "#FEF9C3",
        width: 740,
        render: () => <OutsideTerminal />,
    },
    unnecessary_thoughts: {
        id: "unnecessary_thoughts",
        title: "unnecessary_thoughts // the humor folder",
        label: "unnecessary_thoughts",
        icon: FolderHeart,
        color: "#EC4899",
        tileBg: "#FFE8E8",
        width: 660,
        render: () => <UnnecessaryThoughts />,
    },
    future_exe: {
        id: "future_exe",
        title: "future.exe // beta ambitions",
        label: "future.exe",
        icon: Sparkles,
        color: "#8B5CF6",
        tileBg: "#F3E8FF",
        width: 700,
        render: () => <FutureExe />,
    },
    resume_pdf: {
        id: "resume_pdf",
        title: "resume_final_final_v7.pdf",
        label: "resume_final_final_v7.pdf",
        icon: FileText,
        color: "#10B981",
        tileBg: "#D1FAE5",
        width: 780,
        render: () => <ResumePanel />,
    },
    recycle_bin: {
        id: "recycle_bin",
        title: "recycle bin // where drafts go",
        label: "recycle bin",
        icon: Trash2,
        color: "#64748B",
        tileBg: "#EFECE6",
        width: 640,
        render: () => <RecycleBin />,
    },
    terminal: {
        id: "terminal",
        title: "terminal.exe — krithi.os shell",
        label: "terminal.exe",
        icon: Terminal,
        color: "#1C1917",
        tileBg: "#E2DDD3",
        width: 680,
        render: () => <TerminalApp />,
    },
    tabs: {
        id: "tabs",
        title: "37_tabs_open — simulated browser",
        label: "37_tabs_open",
        icon: Globe,
        color: "#0EA5E9",
        tileBg: "#EFF6FF",
        width: 800,
        render: () => <TabsBrowser />,
    },
    personality: {
        id: "personality",
        title: "personality_settings.exe",
        label: "personality_settings.exe",
        icon: Sliders,
        color: "#F97316",
        tileBg: "#FFEDD5",
        width: 680,
        render: () => <PersonalitySettings />,
    },
    achievements: {
        id: "achievements",
        title: "achievements // progress tracker",
        icon: Trophy,
        color: "#EAB308",
        tileBg: "#FEF08A",
        width: 660,
        render: () => <AchievementsPanel />,
    },
    performance: {
        id: "performance",
        title: "performance_analysis.exe",
        icon: Gauge,
        color: "#FF6B6B",
        tileBg: "#FFE8E8",
        width: 660,
        render: () => <PerformancePanel />,
    },
};

export function getDef(id: string): WindowDef | undefined {
    if (STATIC[id]) return STATIC[id];
    if (id.startsWith("project:")) {
        const p = PROJECTS.find((x) => x.id === id.slice(8));
        if (p)
            return {
                id,
                title: `${p.name} — experiments`,
                icon: p.icon,
                color: p.color,
                tileBg: p.tileBg,
                width: 780,
                render: () => <ProjectWindow projectId={p.id} />,
            };
    }
    if (id.startsWith("file:")) {
        const f = BRAIN_FILES.find((x) => x.id === id.slice(5));
        if (f)
            return {
                id,
                title: f.name,
                icon: f.icon,
                color: "#4A463D",
                tileBg: "#EFECE6",
                width: 620,
                render: () => <FileWindow fileId={f.id} />,
            };
    }
    return undefined;
}
